// Eingabefeld oben in der rechten Spalte (wie in Close als Karte über dem Feed): öffnet sich über die Knöpfe
// „Notiz“ und „Custom Activity ▾“ im Kopf, sonst unsichtbar. Genau eins ist offen: die Notiz oder ein Typ.
// Entwürfe bleiben beim Schließen (Esc, ✕) und beim Wechseln des Leads erhalten; „Abbrechen“ verwirft sie.
// Speichern: Notiz nach Close, Custom Activity im CRM (die Backoffice-Automatik reagiert über die Brücke darauf).
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { aktivitaetenApi, ApiFehler, type Aktivitaet, type Kontakt } from "../../api";
import { entwurfLesen, entwurfSetzen } from "../../daten/entwuerfe";
import { fokusSetzen, nachbarn } from "../../daten/liste";
import { useTextbausteine } from "../../textbausteine/useTextbausteine";
import { modTaste } from "../../werkzeuge/tasten";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { CustomActivityFormular, werteFuerApi, type Werte } from "./CustomActivityFormular";
import { TypName, useCaTypen } from "./CustomActivityMenue";
import { useFollowUpSchreiben } from "./useFollowUpSchreiben";
import { TL } from "./leadTexte";
import { NotizEditor } from "./NotizEditor";
import "../../styles/lead.css";

export const NOTIZ = "notiz";

interface Props {
  leadId: string;
  kontakte: Kontakt[];
  /** offenes Eingabefeld: NOTIZ oder die ID eines Custom-Activity-Typs */
  offen: string;
  onSchliessen: () => void;
  onAktivitaet: (a: Aktivitaet) => void;
}

export function Eingabeleiste({ leadId, kontakte, offen, onSchliessen, onAktivitaet }: Props) {
  const typen = useCaTypen();
  const meldung = useMeldung();
  const navigate = useNavigate();
  const [, setVersion] = useState(0);
  const neuZeichnen = () => setVersion((v) => v + 1);
  const rahmen = useRef<HTMLDivElement>(null);
  const titelFeld = useRef<HTMLInputElement>(null);
  const bausteine = useTextbausteine("notiz");
  const followUpSchreiben = useFollowUpSchreiben();
  const [serverFehler, setServerFehler] = useState<unknown>(null);

  // Beim Öffnen sichtbar machen (die rechte Spalte kann weit nach unten gescrollt sein) und fokussieren
  useEffect(() => {
    setServerFehler(null);
    const el = rahmen.current;
    if (!el) return;
    el.scrollIntoView({ block: "nearest" });
    const t = window.setTimeout(() => {
      if (offen === NOTIZ) el.querySelector<HTMLElement>(".nt__flaeche")?.focus();
      else el.querySelector<HTMLElement>(".ls-caf__felder :is(input:not([type=hidden]), textarea, select, button)")?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(t);
  }, [offen, leadId]);

  const gespeichert = (text: string) => {
    const n = nachbarn(leadId);
    if (n?.nachher) {
      const ziel = n.nachher;
      meldung.ok(text, {
        label: "Nächster Lead",
        onClick: () => {
          fokusSetzen(ziel);
          navigate(`/lead/${encodeURIComponent(ziel)}`);
        },
      });
    } else {
      meldung.ok(text);
    }
  };

  const tastatur = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      // Esc schließt (Entwurf bleibt) und springt nicht zur Liste zurück
      e.preventDefault();
      e.stopPropagation();
      onSchliessen();
    }
  };

  // --- Notiz ---------------------------------------------------------------
  const notizSchluessel = `${leadId}|${NOTIZ}`;
  const titelSchluessel = `${leadId}|${NOTIZ}|titel`;
  const notiz = entwurfLesen<string>(notizSchluessel) ?? "";
  const notizTitel = entwurfLesen<string>(titelSchluessel) ?? "";
  const [notizLaeuft, setNotizLaeuft] = useState(false);
  const notizSpeichern = async () => {
    const html = (entwurfLesen<string>(notizSchluessel) ?? "").trim();
    if (!html) {
      rahmen.current?.querySelector<HTMLElement>(".nt__flaeche")?.focus();
      return;
    }
    setNotizLaeuft(true);
    try {
      const titel = (entwurfLesen<string>(titelSchluessel) ?? "").trim();
      const r = await aktivitaetenApi.notizAnlegen(leadId, { note_html: html, ...(titel ? { title: titel } : {}) });
      entwurfSetzen(notizSchluessel, undefined);
      entwurfSetzen(titelSchluessel, undefined);
      onAktivitaet(r.activity);
      onSchliessen();
      gespeichert("Notiz gespeichert.");
    } catch (e) {
      meldung.fehler(e, "Notiz nicht gespeichert");
    } finally {
      setNotizLaeuft(false);
    }
  };

  if (offen === NOTIZ) {
    return (
      <div ref={rahmen} className="ls-eingabe ls-eingabe--notiz" onKeyDown={tastatur} role="region" aria-label="Notiz schreiben">
        <div className="ls-eingabe__kopf">
          <span className="ls-symbol ls-symbol--note" aria-hidden="true">
            <Icon name="notiz" groesse={13} />
          </span>
          <span className="ls-eingabe__titel">Notiz</span>
          <span className="ls-eingabe__hinweis">geht nach Close</span>
          <button type="button" className="symbolknopf symbolknopf--klein ls-eingabe__zu" title="Schließen (Esc), Text bleibt als Entwurf" aria-label="Schließen" onClick={onSchliessen}>
            <Icon name="schliessen" groesse={14} />
          </button>
        </div>
        <input
          ref={titelFeld}
          className="eingabe ls-eingabe__titelfeld"
          placeholder={TL.notiz.titelPlatzhalter}
          aria-label="Titel der Notiz"
          maxLength={200}
          value={notizTitel}
          onChange={(e) => {
            entwurfSetzen(titelSchluessel, e.target.value);
            neuZeichnen();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (e.metaKey || e.ctrlKey) void notizSpeichern();
              else rahmen.current?.querySelector<HTMLElement>(".nt__flaeche")?.focus();
            }
          }}
        />
        <NotizEditor
          wert={notiz}
          onChange={(html) => {
            entwurfSetzen(notizSchluessel, html);
            neuZeichnen();
          }}
          platzhalter={TL.notiz.textPlatzhalter}
          bausteine={bausteine}
          onStrgEnter={() => void notizSpeichern()}
          mindesthoehe={84}
        />
        <div className="ls-eingabe__fuss">
          <button type="button" className="knopf knopf--primaer" disabled={notizLaeuft || !notiz.trim()} onClick={() => void notizSpeichern()} title={`Speichern (${modTaste} Enter)`}>
            {notizLaeuft ? "Speichert …" : "Speichern"}
          </button>
          <button
            type="button"
            className="knopf knopf--leise"
            title="Text verwerfen und schließen"
            onClick={() => {
              entwurfSetzen(notizSchluessel, undefined);
              entwurfSetzen(titelSchluessel, undefined);
              onSchliessen();
            }}
          >
            Abbrechen
          </button>
          <span className="ls-eingabe__tasten">
            {modTaste} Enter speichert · @ erwähnt · / fügt ein · Esc schließt
          </span>
        </div>
      </div>
    );
  }

  // --- Custom Activity -----------------------------------------------------
  const typ = typen.find((t) => t.id === offen) ?? null;
  if (!typ) return null;
  const caSchluessel = `${leadId}|${typ.id}`;
  let werte: Werte = {};
  const vorhanden = entwurfLesen<Werte>(caSchluessel);
  if (vorhanden) werte = vorhanden;
  else {
    // Vorbelegung: Pflicht-Kontaktfeld bei genau einem Kontakt
    for (const f of typ.fields) {
      if (f.type === "contact" && f.required && kontakte.length === 1) werte[f.id] = f.accepts_multiple_values ? [kontakte[0].id] : kontakte[0].id;
    }
  }

  const caSpeichern = async (status: "published" | "draft") => {
    setServerFehler(null);
    try {
      const r = await aktivitaetenApi.customAnlegen(leadId, { custom_activity_type_id: typ.id, status, fields: werteFuerApi(typ, werte) });
      entwurfSetzen(caSchluessel, undefined);
      const followUp = await followUpSchreiben(leadId, typ, werte, status);
      onAktivitaet(r.activity);
      onSchliessen();
      gespeichert(status === "draft" ? `Als Entwurf abgelegt: ${typ.name}` : `Gespeichert: ${typ.name}${followUp ? ", Follow-UP Datum gesetzt" : ""}`);
    } catch (e) {
      if (e instanceof ApiFehler && e.code === "pflichtfelder_fehlen") setServerFehler(e);
      meldung.fehler(e, "Nicht gespeichert");
    }
  };

  return (
    <div ref={rahmen} className="ls-eingabe" onKeyDown={tastatur} role="region" aria-label={`Custom Activity: ${typ.name}`}>
      <div className="ls-eingabe__kopf">
        <span className="ls-symbol ls-symbol--custom" aria-hidden="true">
          <Icon name="aktivitaet" groesse={13} />
        </span>
        <span className="ls-eingabe__titel">
          <TypName name={typ.name} />
        </span>
        <span
          className="ls-eingabe__hinweis"
          title="Wird im CRM gespeichert, nicht in Close. Die Backoffice-Automatik sieht den Eintrag über die Brücke und reagiert darauf (z. B. Status, Score, Mail oder Einladung). Entwürfe sieht sie nicht."
        >
          im CRM · Automatik reagiert
        </span>
        <button type="button" className="symbolknopf symbolknopf--klein ls-eingabe__zu" title="Schließen (Esc), Eingaben bleiben als Entwurf" aria-label="Schließen" onClick={onSchliessen}>
          <Icon name="schliessen" groesse={14} />
        </button>
      </div>
      <CustomActivityFormular
        key={`${leadId}|${typ.id}`}
        typ={typ}
        kontakte={kontakte}
        werte={werte}
        followUp
        serverFehler={serverFehler}
        onChange={(w) => {
          entwurfSetzen(caSchluessel, w);
          neuZeichnen();
        }}
        onSpeichern={caSpeichern}
        verwerfenText="Abbrechen"
        tastenHinweis={`${modTaste} Enter speichert · Esc schließt`}
        onVerwerfen={() => {
          entwurfSetzen(caSchluessel, undefined);
          setServerFehler(null);
          onSchliessen();
        }}
      />
    </div>
  );
}
